import {
  type AgentTask,
  AgentTaskStatus,
} from "@/shared/domain/agentThread/AgentThreadRecord";

import { type MessageGroup } from "./groupToolMessages";

/**
 * Tool names that are pure task-list machinery and should not appear in the
 * transcript.
 *
 * The live set mirrors the backend `get_name()` values of the task tools in
 * `packages/roboto_service/src/roboto_service/ai/tools/tasks.py`
 * (AddTasksTool / ChangeTaskTool / AddSubtasksTool) — there is no shared
 * cross-repo contract, so the set is pinned from both sides:
 * `taskSections.test.ts` pins this exact set, and `test_tasks.py` pins the
 * backend names against it. Renaming or adding a task tool requires updating
 * this set, and the backend pin test fails loudly if it is forgotten.
 *
 * `start_task` and `complete_task` are LEGACY names: the backend unified them
 * into `change_task`, but threads created before that deploy still carry their
 * tool_use blocks in history. They are kept here so those historical calls stay
 * hidden from the transcript; the backend no longer emits them.
 */
export const HIDDEN_TASK_TOOL_NAMES: ReadonlySet<string> = new Set([
  "add_tasks",
  "change_task",
  "add_subtasks",
  // Legacy (pre-change_task) — retained so old threads keep hiding them.
  "start_task",
  "complete_task",
]);

/** A run of consecutive segments that belong to one started task, to be wrapped in a collapsible. */
export interface TaskBlock {
  kind: "task";
  taskId: number;
  title: string;
  /** Whether the owning task has completed. Drives the section header's status
   *  icon (done check vs. in-progress indicator); the section is rendered for
   *  every started task regardless, so this never gates wrapping. */
  concluded: boolean;
  segments: MessageGroup[];
}

/** A segment that belongs to no started task and renders inline. */
export interface LooseBlock {
  kind: "loose";
  segment: MessageGroup;
}

export type RenderBlock = TaskBlock | LooseBlock;

/**
 * A contiguous region of the conversation owned by one started task. A region
 * spans `[start, end]` inclusive: from the message that started the task through
 * the message whose tool call completed it. With the unified `change_task` tool
 * one call completes task A and starts task B, so A's `end` and B's `start` are
 * the same message — ownership of that shared message is split per segment (see
 * `regionForSegment`). Messages after `end` that no region starts on — a
 * trailing wrap-up, later user turns — belong to no region and render loose.
 *
 * `end` is null for an in-progress task, whose region stays open to the end of
 * the thread (until the next task starts). Its turns render inside the task's
 * still-running section, which is created the moment the task starts and stays
 * expanded so live work is visible.
 */
interface TaskRegion {
  taskId: number;
  title: string;
  concluded: boolean;
  start: number;
  /** Tie-breaker for tasks started in the same message (same `start`): the
   *  tool-call block index, so ordering and ownership stay deterministic. */
  startContent: number;
  /** Last message owned, inclusive; null while the task is still in progress. */
  end: number | null;
}

/**
 * Build the task regions for a thread, ordered by start position, from every
 * started task. Ties on the start message are broken by the tool-call block
 * index (`content_sequence_num`) so two tasks started in one assistant message
 * have a stable order.
 */
export function buildTaskRegions(
  tasks: AgentTask[] | null | undefined,
): TaskRegion[] {
  const regions: TaskRegion[] = [];
  for (const task of tasks ?? []) {
    // A started task (concluded or in-progress) has a start boundary; pending
    // tasks have none and own no conversation yet.
    if (task.start) {
      regions.push({
        taskId: task.task_id,
        title: task.title,
        concluded: task.status === AgentTaskStatus.COMPLETED,
        start: task.start.message_sequence_num,
        startContent: task.start.content_sequence_num,
        end: task.end?.message_sequence_num ?? null,
      });
    }
  }
  regions.sort((a, b) => a.start - b.start || a.startContent - b.startContent);
  return regions;
}

/**
 * The region that owns a message index: among regions whose span `[start, end]`
 * covers `index`, the last in sort order (greatest start, then greatest content).
 * Returns undefined for a message past every region's end — a loose trailing
 * turn that belongs to no task.
 */
function regionAt(
  regions: TaskRegion[],
  index: number,
): TaskRegion | undefined {
  let owner: TaskRegion | undefined;
  for (const region of regions) {
    // Regions are sorted by start; once a start is past index, so is the rest.
    if (region.start > index) {
      break;
    }
    if (region.end === null || index <= region.end) {
      owner = region;
    }
  }
  return owner;
}

/**
 * The region that owns a segment: normally the region owning the segment's
 * first message index, with one exception — the leading text of a task-handoff
 * message. A unified `change_task` call completes task A and starts task B at
 * one position, so A's end and B's start are the same message, and the model
 * writes A's closing summary (often the task's actual verdict) as a text block
 * in that message, before the tool call. A `messages` segment anchored where
 * one region ends and another starts therefore belongs to the ENDING region;
 * the tool group at the same index (and everything after) belongs to the
 * starting one. Without the exception, `regionAt` hands the whole message to B
 * and A's closing words render inside B's collapsible.
 */
function regionForSegment(
  regions: TaskRegion[],
  segment: MessageGroup,
): TaskRegion | undefined {
  const firstIndex = segment.messageIndices[0];
  if (firstIndex === undefined) {
    return undefined;
  }
  const owner = regionAt(regions, firstIndex);
  if (!owner || segment.type !== "messages" || owner.start !== firstIndex) {
    return owner;
  }
  // Latest-starting region that ends where `owner` starts — excluding `owner`
  // itself, which a single-message region (start === end) would otherwise match.
  let ending: TaskRegion | undefined;
  for (const region of regions) {
    if (region.start > firstIndex) {
      break;
    }
    if (region.end === firstIndex && region !== owner) {
      ending = region;
    }
  }
  return ending ?? owner;
}

/** True when `index` is the first message of a different task region than `index - 1`. */
export function isTaskRegionBoundary(
  regions: TaskRegion[],
  index: number,
): boolean {
  if (regions.length === 0) {
    return false;
  }
  return (
    regionAt(regions, index)?.taskId !== regionAt(regions, index - 1)?.taskId
  );
}

/**
 * Group conversation segments into render blocks, wrapping the consecutive
 * segments of each started task — in-progress or concluded — so the view can
 * render them inside one collapsible.
 *
 * The section is created when the task starts, not when it completes: a task's
 * turns stream into its (open) section live, and completion only flips the
 * header's status icon — it never re-buckets turns from loose into a section.
 * Wrapping only on completion (the previous behavior) forced exactly that
 * re-bucket mid-stream, and against a live-growing message array whose
 * task-boundary seq-nums briefly disagree with array positions, it left a task's
 * early turns stranded loose and could render a turn both loose and inside the
 * freshly-formed section (the duplication that a reload — one consistent
 * snapshot grouped once — always cleared).
 *
 * Each segment is assigned via `regionForSegment` (the region owning its first
 * message index, except a handoff message's leading text, which goes to the
 * task ending there); because the segments were produced with the matching
 * boundary predicate (so none spans a region), the first index is
 * representative of the whole segment. Segments owned by no region — planning
 * before any task, a trailing wrap-up after a task's end boundary — stay loose.
 */
export function groupSegmentsByStartedTask(
  segments: MessageGroup[],
  regions: TaskRegion[],
): RenderBlock[] {
  if (regions.length === 0) {
    return segments.map((segment) => ({ kind: "loose", segment }));
  }

  const blocks: RenderBlock[] = [];
  let current: TaskBlock | null = null;

  const flush = () => {
    if (current) {
      blocks.push(current);
      current = null;
    }
  };

  for (const segment of segments) {
    const region = regionForSegment(regions, segment);

    if (!region) {
      flush();
      blocks.push({ kind: "loose", segment });
      continue;
    }

    if (current && current.taskId === region.taskId) {
      current.segments.push(segment);
    } else {
      flush();
      current = {
        kind: "task",
        taskId: region.taskId,
        title: region.title,
        concluded: region.concluded,
        segments: [segment],
      };
    }
  }
  flush();

  return blocks;
}

/**
 * Whether a segment renders any visible content. A tool group made up entirely
 * of hidden task-machinery calls renders nothing (the view's `renderSegment`
 * filters those tool results out and returns null); a message segment always
 * carries at least one real message.
 */
function segmentHasVisibleContent(segment: MessageGroup): boolean {
  if (segment.type === "toolGroup") {
    return segment.toolResults.some(
      (toolResult) => !HIDDEN_TASK_TOOL_NAMES.has(toolResult.tool_name),
    );
  }
  return segment.messages.length > 0;
}

/**
 * Drop task blocks whose every segment is hidden task machinery. Such a block
 * (e.g. a task started — and perhaps completed — with no real work between, or a
 * task whose only turn so far is its `start_task` call) would otherwise render a
 * TaskSection header and chevron that expands to an empty body; pruning defers
 * the section until its first visible turn streams in. Loose blocks render
 * inline with no wrapper, so an empty one produces nothing visible and is left
 * untouched.
 */
export function pruneEmptyTaskBlocks(blocks: RenderBlock[]): RenderBlock[] {
  return blocks.filter(
    (block) =>
      block.kind !== "task" || block.segments.some(segmentHasVisibleContent),
  );
}

/**
 * Find the task-section block that owns a given message, if any.
 *
 * Every started task renders inside a collapsible whose body is unmounted while
 * the user has it closed, so a "scroll to message N" consumer (the admin thread
 * explorer) cannot find the target node when it lives in a closed section. The
 * view uses this to auto-expand the owning section before the scroll fires.
 *
 * Returns the owning task id, or ``undefined`` when the message renders loose
 * (inline) or falls outside every task-section block.
 */
export function sectionTaskIdForMessage(
  blocks: RenderBlock[],
  messageSequenceNum: number,
): number | undefined {
  for (const block of blocks) {
    if (block.kind !== "task") {
      continue;
    }
    const owns = block.segments.some((segment) =>
      segment.messageIndices.includes(messageSequenceNum),
    );
    if (owns) {
      return block.taskId;
    }
  }
  return undefined;
}
